import {
  Dispatch,
  SetStateAction,
  useState,
} from 'react';
import {
  DocumentTextIcon,
  PaperClipIcon,
  ArrowTopRightOnSquareIcon,
  ChevronDownIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';

import { Badge } from '../../../../common/Atoms/Badge';
import { Paragraph } from '../../../../common/Atoms/Typography/Paragraph';
import { classNames } from '../../../../common/lib/classNames';
import { WrappedLink } from '../../WrappedLink';
import { useInboundEmailAttachmentDownload } from '../../../hooks/useInboundEmailAttachmentDownload';
import {
  AgentConversationNote,
  AgentMessage,
  AgentRun,
  AgentTask,
} from '../../../services/api/aiAgentApi/aiAgent';
import {
  describeToolActivity,
  friendlyRole,
  getToolStatusMeta,
} from '../lib/agentDisplay';

import { formatLocalTimestamp } from './conversationHelpers';
import { PendingTaskApproval } from './PendingTaskApproval';
import { SendToolReadOnlyDetails } from './SendToolReadOnlyDetails';
import { TaskEditValues } from './taskEditHelpers';

export interface TranscriptItemProps {
  kind?: `message` | `note`;
  note?: AgentConversationNote;
  message?: AgentMessage;
  activity?: ReturnType<typeof describeToolActivity>;
  task?: AgentTask;
  customerLabel?: string;
  taskEdits?: Record<string, TaskEditValues>;
  setTaskEdits?: Dispatch<SetStateAction<Record<string, TaskEditValues>>>;
  isDecidingTask?: boolean;
  pendingDecisionTaskId?: string | null;
  isProcessing?: boolean;
  onDecideTask?: (task: AgentTask, approved: boolean, autoApproveTool?: boolean) => void;
  onSkipAndResolve?: (task: AgentTask) => void;
  actionId?: string | null;
  invoiceId?: string | null;
  onViewAction?: (actionId: string) => void;
  attachments?: AgentRun[`attachments`];
  inboundEmailId?: string | null;
}

export function TranscriptItem({
  message,
  activity,
  task,
  customerLabel,
  taskEdits,
  setTaskEdits,
  isDecidingTask,
  pendingDecisionTaskId,
  isProcessing,
  onDecideTask,
  onSkipAndResolve,
  actionId,
  invoiceId,
  onViewAction,
  kind = `message`,
  note,
  attachments = [],
  inboundEmailId,
}: TranscriptItemProps) {
  const [showToolDetails, setShowToolDetails] = useState(false);
  const { downloadAttachment, downloading } = useInboundEmailAttachmentDownload();

  if (kind === `note` && note) {
    return (
      <div className={ `rounded-lg border border-amber-200 bg-amber-50/50 p-3` }>
        <div className={ `flex items-center justify-between gap-2` }>
          <div className={ `flex items-center gap-2` }>
            <DocumentTextIcon className={ `h-4 w-4 text-amber-700` } />
            <Paragraph
              size={ `xs` }
              className={ `font-medium text-amber-900` }
            >
              { `Internal note` }
            </Paragraph>
            <Paragraph
              size={ `xs` }
              variant={ `secondary` }
            >
              { note.author_name || `Unknown user` }
            </Paragraph>
          </div>
          <span className={ `text-xs text-amber-700/80` }>
            { formatLocalTimestamp(note.created_at) }
          </span>
        </div>
        <Paragraph
          size={ `sm` }
          className={ `mt-2 whitespace-pre-wrap text-amber-950` }
        >
          { note.content }
        </Paragraph>
        {
          note.mentions.length > 0 &&
          <Paragraph
            size={ `xs` }
            className={ `mt-2 text-amber-800` }
          >
            { `Mentioned: ${note.mentions.map(mention => mention.name || mention.email || `Unknown user`).join(`, `)}` }
          </Paragraph>
        }
      </div>
    );
  }

  if (!message) {
    return null;
  }

  if (message.item_type === `function_call`) {
    if (!activity) {
      return null;
    }

    const resolvedActionId = actionId || activity.actionId;
    const statusMeta = getToolStatusMeta(task);
    const showAttentionBadge = statusMeta?.label === `Awaiting approval`;
    const isPendingApprovalTask = task?.approval_status === `pending`;
    const isSendTool = [
      `send_email_to_customer`,
      `send_sms_to_customer`,
      `resend_invoice_email`,
      `send_statement`,
    ].includes(task?.tool_name || ``);
    const showSendToolDetails = Boolean(task) && isSendTool && task?.execution_status === `executed`;
    const hasSummary = Boolean(!isSendTool && activity.summary);
    const hasDetails = showAttentionBadge || hasSummary;

    // Keep the prominent card only while a task is awaiting a human decision
    // (that state is actionable). Everything else renders as a subtle,
    // collapsible one-line tool-usage row, similar to Cursor/Claude/ChatGPT.
    if (isPendingApprovalTask) {
      return (
        <div className={ `flex justify-end` }>
          <div className={ `w-full max-w-[85%] rounded-lg border border-orange-200 bg-orange-50/40 p-3` }>
            <div className={ `flex flex-wrap items-center justify-between gap-2` }>
              <div className={ `flex min-w-0 items-center gap-2` }>
                <WrenchScrewdriverIcon
                  className={ `h-4 w-4 text-gray-500` }
                  title={ `Used a tool` }
                />
                <div className={ `min-w-0` }>
                  <div className={ `flex items-center gap-2` }>
                    <Paragraph size={ `sm` }>
                      { activity.label }
                    </Paragraph>
                    {
                      resolvedActionId && onViewAction &&
                      <button
                        type={ `button` }
                        className={ `rounded-sm p-0.5 text-gray-500 transition hover:bg-white hover:text-gray-700` }
                        title={ `View action` }
                        aria-label={ `View action` }
                        onClick={ () => onViewAction(resolvedActionId) }
                      >
                        <ArrowTopRightOnSquareIcon className={ `h-4 w-4` } />
                      </button>
                    }
                    {
                      invoiceId &&
                      <WrappedLink
                        to={ `/invoices/${invoiceId}` }
                        className={ `text-xs text-gray-500 underline underline-offset-2 transition hover:text-gray-700 hover:no-underline` }
                      >
                        { `Invoice` }
                      </WrappedLink>
                    }
                    {
                      showAttentionBadge &&
                      <Badge color={ statusMeta.color }>
                        { statusMeta.label }
                      </Badge>
                    }
                  </div>
                </div>
              </div>
              <span className={ `text-xs text-gray-400` }>
                { formatLocalTimestamp(message.created_at) }
              </span>
            </div>

            {
              hasDetails &&
              <button
                type={ `button` }
                className={ `mt-2 flex items-center gap-1 text-xs text-gray-600 hover:text-gray-800` }
                onClick={ () => setShowToolDetails(prev => !prev) }
              >
                <ChevronDownIcon
                  className={ classNames(
                    `h-3.5 w-3.5 transition-transform`,
                    showToolDetails && `rotate-180`,
                  ) }
                />
                { showToolDetails ? `Hide details` : `Show details` }
              </button>
            }
            {
              hasDetails && showToolDetails &&
              <div className={ `mt-2 space-y-1 rounded-md border border-gray-200 bg-white p-2` }>
                {
                  statusMeta?.label === `Awaiting approval` &&
                  <Paragraph
                    size={ `xs` }
                    variant={ `secondary` }
                  >
                    { `Waiting for your approval before this is sent.` }
                  </Paragraph>
                }
                {
                  hasSummary &&
                  <Paragraph
                    size={ `xs` }
                    variant={ `secondary` }
                  >
                    { activity.summary.replace(/\s*\n\s*/g, ` · `) }
                  </Paragraph>
                }
              </div>
            }
            {
              onDecideTask && onSkipAndResolve &&
              <div className={ `mt-3` }>
                <PendingTaskApproval
                  task={ task }
                  taskEdits={ taskEdits }
                  setTaskEdits={ setTaskEdits }
                  isDecidingTask={ isDecidingTask }
                  pendingDecisionTaskId={ pendingDecisionTaskId }
                  isProcessing={ isProcessing }
                  onDecideTask={ onDecideTask }
                  onSkipAndResolve={ onSkipAndResolve }
                />
              </div>
            }
          </div>
        </div>
      );
    }

    const expandable = hasSummary || showSendToolDetails;

    return (
      <div className={ `flex justify-end` }>
        <div className={ `w-full max-w-[85%]` }>
          <div className={ `flex items-center justify-end gap-1.5 text-gray-400` }>
            <WrenchScrewdriverIcon
              className={ `h-3.5 w-3.5 shrink-0` }
              title={ `Used a tool` }
            />
            {
              expandable ?
                <button
                  type={ `button` }
                  className={ `flex min-w-0 items-center gap-1 text-sm text-gray-400 transition hover:text-gray-600` }
                  onClick={ () => setShowToolDetails(prev => !prev) }
                >
                  <span className={ `truncate` }>
                    { activity.label }
                  </span>
                  <ChevronDownIcon
                    className={ classNames(
                      `h-3 w-3 shrink-0 transition-transform`,
                      showToolDetails && `rotate-180`,
                    ) }
                  />
                </button> :
                <span className={ `truncate text-sm text-gray-400` }>
                  { activity.label }
                </span>
            }
            {
              resolvedActionId && onViewAction &&
              <button
                type={ `button` }
                className={ `rounded-sm p-0.5 text-gray-400 transition hover:text-gray-600` }
                title={ `View action` }
                aria-label={ `View action` }
                onClick={ () => onViewAction(resolvedActionId) }
              >
                <ArrowTopRightOnSquareIcon className={ `h-3.5 w-3.5` } />
              </button>
            }
            {
              invoiceId &&
              <WrappedLink
                to={ `/invoices/${invoiceId}` }
                className={ `text-xs text-gray-400 underline underline-offset-2 transition hover:text-gray-600 hover:no-underline` }
              >
                { `Invoice` }
              </WrappedLink>
            }
            <span className={ `text-xs text-gray-300` }>
              { formatLocalTimestamp(message.created_at) }
            </span>
          </div>
          {
            expandable && showToolDetails &&
            <div className={ `mt-1.5 space-y-1 border-l-2 border-gray-100 pl-3` }>
              { showSendToolDetails && <SendToolReadOnlyDetails task={ task } /> }
              {
                hasSummary &&
                <Paragraph
                  size={ `xs` }
                  variant={ `secondary` }
                >
                  { activity.summary.replace(/\s*\n\s*/g, ` · `) }
                </Paragraph>
              }
            </div>
          }
        </div>
      </div>
    );
  }

  if ((message.item_type !== `message` && message.item_type !== `narration`) || !message.content?.trim()) {
    return null;
  }

  const isCustomer = message.role === `user`;
  const isAgent = message.role === `assistant`;
  const isStaff = message.role === `staff`;

  if (!isCustomer && !isAgent && !isStaff) {
    return (
      <div className={ `flex justify-center` }>
        <span className={ `max-w-[85%] text-center text-xs text-gray-400 italic` }>
          { message.content }
        </span>
      </div>
    );
  }

  if (isAgent) {
    return (
      <div className={ `flex justify-end` }>
        <div className={ `w-full space-y-1` }>
          <p className={ `whitespace-pre-wrap text-xs italic font-light text-gray-400 text-right` }>
            { message.content }
          </p>
        </div>
      </div>
    );
  }

  if (isStaff) {
    const staffLabel = customerLabel || `Staff`;

    return (
      <div className={ classNames(`flex`, `justify-end`) }>
        <div className={ classNames(`max-w-[85%] space-y-1`, `items-end`) }>
          <div className={ classNames(`flex items-center justify-end gap-2`) }>
            <Paragraph
              size={ `xs` }
              variant={ `secondary` }
            >
              { staffLabel }
            </Paragraph>
            <span className={ `text-xs text-gray-400` }>
              { formatLocalTimestamp(message.created_at) }
            </span>
          </div>
          <div
            className={ classNames(
              `whitespace-pre-wrap rounded-2xl border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm text-indigo-950`,
            ) }
          >
            { message.content }
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={ classNames(`flex`, `justify-start`) }>
      <div className={ classNames(`max-w-[85%] space-y-1`, `items-start`) }>
        <div className={ classNames(`flex items-center gap-2`) }>
          <Paragraph
            size={ `xs` }
            variant={ `secondary` }
          >
            { customerLabel || friendlyRole(message.role) }
          </Paragraph>
          <span className={ `text-xs text-gray-400` }>
            { formatLocalTimestamp(message.created_at) }
          </span>
        </div>
        <div
          className={ classNames(
            `whitespace-pre-wrap rounded-2xl px-3 py-2 text-sm`,
            `bg-gray-100 text-gray-800`,
          ) }
        >
          { message.content }
        </div>
        {
          attachments.length > 0 && inboundEmailId &&
          <div className={ `rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-600` }>
            <div className={ `flex items-center gap-1 text-gray-700` }>
              <PaperClipIcon className={ `h-3.5 w-3.5` } />
              <span className={ `font-medium` }>{ `Attachments:` }</span>
            </div>
            <div className={ `mt-1 flex flex-wrap gap-x-2 gap-y-1` }>
              {
                attachments.map(attachment => {
                  const isDownloading = downloading?.inboundEmailId === inboundEmailId && downloading.index === attachment.index;

                  return (
                    <button
                      key={ `${attachment.index}-${attachment.name}` }
                      type={ `button` }
                      className={ `cursor-pointer text-indigo-700 underline underline-offset-2 transition-colors duration-200 hover:text-indigo-900 disabled:cursor-not-allowed disabled:text-gray-500 disabled:no-underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-indigo-300` }
                      onClick={ () => {
                        void downloadAttachment(inboundEmailId, attachment.index, attachment.name);
                      } }
                      disabled={ isDownloading }
                    >
                      { isDownloading ? `${attachment.name} (downloading...)` : attachment.name }
                    </button>
                  );
                })
              }
            </div>
          </div>
        }
      </div>
    </div>
  );
}
